/**
 * Decision detail dialog — trader-facing "why did Haiku decide this?"
 * view. Plain-English narrative first, numeric context second, raw
 * prompt/response tucked behind a Developer toggle for when something
 * genuinely needs dev-level inspection.
 */

import { useEffect, useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2,
  AlertTriangle,
  Shield,
  Clock,
  TrendingUp,
  Newspaper,
  CheckCircle2,
  Ban,
  Pause,
  Zap,
  Code2,
} from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { getDecision, type DecisionDetail } from "@/lib/api/decisions";

interface Props {
  decisionId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const actionMeta: Record<
  string,
  { color: string; label: string; icon: typeof Zap; tagline: string }
> = {
  CONTINUE: {
    color: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    label: "No Action",
    icon: CheckCircle2,
    tagline: "The bot checked in, saw nothing needed doing, and moved on.",
  },
  HOLD: {
    color: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    label: "Holding",
    icon: Pause,
    tagline: "The bot is deliberately sitting on its hands until a specific time.",
  },
  EXECUTE: {
    color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    label: "Acted",
    icon: Zap,
    tagline: "The bot took an action against the broker.",
  },
  ALERT: {
    color: "bg-destructive/10 text-destructive border-destructive/20",
    label: "Alert Raised",
    icon: AlertTriangle,
    tagline: "A hard safety condition was hit — the bot blocked action and flagged you.",
  },
  CLOSE: {
    color: "bg-destructive/10 text-destructive border-destructive/20",
    label: "Closed Pair",
    icon: Ban,
    tagline: "The bot closed the hedge pair.",
  },
  ESCALATE: {
    color: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    label: "Escalated",
    icon: AlertTriangle,
    tagline: "Haiku wasn't sure — the decision escalated for a deeper review.",
  },
  BLOCK: {
    color: "bg-destructive/10 text-destructive border-destructive/20",
    label: "Blocked",
    icon: Ban,
    tagline: "A rule check failed — the requested action was denied.",
  },
};

export function DecisionDetailDialog({ decisionId, open, onOpenChange }: Props): ReactNode {
  const [detail, setDetail] = useState<DecisionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDev, setShowDev] = useState(false);

  useEffect(() => {
    if (!open || decisionId == null) {
      setDetail(null);
      setError(null);
      setShowDev(false);
      return;
    }
    setLoading(true);
    setError(null);
    getDecision(decisionId)
      .then((d) => setDetail(d))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [open, decisionId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-light">Decision Explained</DialogTitle>
          <DialogDescription className="text-[11px] text-muted-foreground">
            {detail
              ? `${new Date(detail.created_at).toLocaleString()} · ${detail.symbol ?? detail.pair_id}`
              : ""}
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center py-12 text-sm text-muted-foreground"
            >
              <Loader2 className="mr-2 h-4 w-4 animate-spin text-accent" />
              Loading…
            </motion.div>
          ) : error ? (
            <motion.div
              key="error"
              className="flex items-center gap-2 py-12 text-sm text-destructive"
            >
              <AlertTriangle className="h-4 w-4" />
              {error}
            </motion.div>
          ) : detail ? (
            <motion.div
              key="detail"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex flex-col gap-5"
            >
              <Verdict detail={detail} />
              <Narrative detail={detail} />
              <WhatBotSaw detail={detail} />
              <ComplianceCallout detail={detail} />
              <DevToggle show={showDev} onToggle={() => setShowDev((v) => !v)} detail={detail} />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────

/** Big headline — tells the trader WHAT happened in one glance. */
function Verdict({ detail }: { detail: DecisionDetail }): ReactNode {
  const key = detail.claude_action.toUpperCase();
  const meta = actionMeta[key] ?? {
    color: "bg-muted text-foreground border-border",
    label: key,
    icon: CheckCircle2,
    tagline: "",
  };
  const Icon = meta.icon;
  return (
    <section
      className={cn(
        "flex items-start gap-3 rounded-xl border p-4",
        meta.color.replace(/text-\S+/, "").replace(/bg-\S+/, "bg-muted/40"),
      )}
    >
      <div
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border",
          meta.color,
        )}
      >
        <Icon className="h-4.5 w-4.5" />
      </div>
      <div className="flex flex-col gap-0.5">
        <div className="text-base font-medium text-foreground">{meta.label}</div>
        <div className="text-[11.5px] text-muted-foreground">{meta.tagline}</div>
        {detail.confidence != null && (
          <div className="mt-1 text-[10.5px] text-muted-foreground">
            Confidence: <span className="font-mono text-foreground">{Math.round(detail.confidence * 100)}%</span>
          </div>
        )}
      </div>
    </section>
  );
}

/** Plain-English reason — Haiku's own words, foregrounded. */
function Narrative({ detail }: { detail: DecisionDetail }): ReactNode {
  return (
    <section className="flex flex-col gap-2">
      <Label>Why</Label>
      <p className="rounded-lg bg-muted/30 p-3 text-xs leading-relaxed text-foreground/90">
        {detail.reason}
      </p>
    </section>
  );
}

/**
 * What the bot checked before deciding. Each item becomes a one-line
 * English sentence — no JSON, no epoch ms. If the model listed a rule
 * we don't have a friendly label for, fall back to the raw token so
 * nothing is hidden, just less polished.
 */
function WhatBotSaw({ detail }: { detail: DecisionDetail }): ReactNode {
  const rules = parseRulesChecked(detail.rules_checked);
  if (rules.length === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <Label>What The Bot Checked</Label>
      <ul className="flex flex-col gap-1.5">
        {rules.map((r) => {
          const meta = ruleMeta(r);
          const Icon = meta.icon;
          return (
            <li
              key={r}
              className="flex items-start gap-2 rounded-md border border-border/40 bg-muted/20 px-3 py-2 text-[11.5px]"
            >
              <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <div>
                <div className="text-foreground">{meta.label}</div>
                {meta.hint && <div className="text-[10.5px] text-muted-foreground">{meta.hint}</div>}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Only render when there's something actionable to flag. */
function ComplianceCallout({ detail }: { detail: DecisionDetail }): ReactNode {
  if (!detail.compliance_status || detail.compliance_status === "ok") return null;
  return (
    <section className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
      <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" />
      <div className="flex flex-col gap-0.5">
        <div className="text-xs font-medium text-amber-500">
          Rule Check: {detail.compliance_status}
        </div>
        {detail.violation_detail && (
          <div className="text-[11px] text-amber-500/80">{detail.violation_detail}</div>
        )}
      </div>
    </section>
  );
}

/** Collapsed dev section — only someone who asked sees the raw JSON. */
function DevToggle({
  show,
  onToggle,
  detail,
}: {
  show: boolean;
  onToggle: () => void;
  detail: DecisionDetail;
}): ReactNode {
  const totalTokens =
    (detail.usage_input ?? 0) +
    (detail.usage_cache_read ?? 0) +
    (detail.usage_cache_write ?? 0) +
    (detail.usage_output ?? 0);
  return (
    <section className="mt-2 rounded-lg border border-border/40 bg-muted/15">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
      >
        <span className="flex items-center gap-2">
          <Code2 className="h-3 w-3" />
          Developer Details
          <span className="font-mono text-[10px]">
            · audit #{detail.id} · {totalTokens ? `${totalTokens.toLocaleString()} tok` : "—"} ·{" "}
            {detail.cost_usd != null ? `$${detail.cost_usd.toFixed(5)}` : "—"}
          </span>
        </span>
        <span className="text-[10px]">{show ? "hide" : "show"}</span>
      </button>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-2 px-3 pb-3 pt-1">
              <KVRow label="Tier" value={detail.tier} />
              <KVRow label="Firm" value={detail.firm_id} />
              <KVRow label="Executed" value={detail.executed ? "yes" : "no"} />
              <KVRow label="Request ID" value={detail.request_id ?? "—"} mono />
              <KVRow label="Rulebook hash" value={detail.firm_rulebook_hash?.slice(0, 16) ?? "—"} mono />
              <KVRow
                label="Tokens"
                value={`in ${detail.usage_input ?? 0} · cache-read ${detail.usage_cache_read ?? 0} · cache-write ${detail.usage_cache_write ?? 0} · out ${detail.usage_output ?? 0}`}
                mono
              />
              {detail.raw_prompt && (
                <details className="rounded border border-border/50 bg-background/40">
                  <summary className="cursor-pointer px-2 py-1 text-[10.5px] text-muted-foreground">
                    Raw prompt ({(detail.raw_prompt.length / 1024).toFixed(1)} KB)
                  </summary>
                  <pre className="max-h-[40vh] overflow-auto px-2 pb-2 text-[10px] leading-relaxed text-muted-foreground">
                    {safePretty(detail.raw_prompt)}
                  </pre>
                </details>
              )}
              {detail.raw_response && (
                <details className="rounded border border-border/50 bg-background/40">
                  <summary className="cursor-pointer px-2 py-1 text-[10.5px] text-muted-foreground">
                    Raw response ({(detail.raw_response.length / 1024).toFixed(1)} KB)
                  </summary>
                  <pre className="max-h-[40vh] overflow-auto px-2 pb-2 text-[10px] leading-relaxed text-muted-foreground">
                    {safePretty(detail.raw_response)}
                  </pre>
                </details>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// ─── helpers ────────────────────────────────────────────────────

function Label({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

function KVRow({ label, value, mono }: { label: string; value: string; mono?: boolean }): ReactNode {
  return (
    <div className="flex justify-between gap-3 text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("text-foreground", mono && "font-mono")}>{value}</span>
    </div>
  );
}

/** Map a rule-check token to a trader-friendly label + icon. Unknown
 *  tokens fall through as-is so we never hide information. */
function ruleMeta(rule: string): {
  icon: typeof Clock;
  label: string;
  hint?: string;
} {
  const k = rule.toLowerCase();
  if (k.includes("window")) {
    return {
      icon: Clock,
      label: "Trading window timing",
      hint: "Current UTC time vs. today's randomized window start and force-close cutoff.",
    };
  }
  if (k.includes("weekend")) {
    return {
      icon: Clock,
      label: "Weekend status",
      hint: "Bots are fully off Saturday and Sunday UTC.",
    };
  }
  if (k.includes("news") || k.includes("blackout")) {
    return {
      icon: Newspaper,
      label: "News blackout window",
      hint: "Red-zone events affecting this symbol's currencies in the next 2 hours.",
    };
  }
  if (k.includes("daily") || k.includes("dd") || k.includes("drawdown")) {
    return {
      icon: TrendingUp,
      label: "Drawdown buffer",
      hint: "How much of the daily and total drawdown headroom is still available.",
    };
  }
  if (k.includes("position") || k.includes("open")) {
    return {
      icon: Shield,
      label: "Current positions",
      hint: "Whether either leg already has an open ticket, SL, or TP.",
    };
  }
  if (k.includes("rule") || k.includes("firm")) {
    return {
      icon: Shield,
      label: "Firm rulebook",
      hint: "Per-account overrides and the firm's published trading rules.",
    };
  }
  return { icon: Shield, label: rule };
}

function parseRulesChecked(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p.map(String) : [raw];
  } catch {
    return [raw];
  }
}

function safePretty(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return raw;
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return raw;
  }
}
